import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, userApi, saveUserBrowserState, loadUserBrowserState, invalidateAccountInfo } from '@/services/api';
import { useUserStore } from '@/stores/userStore';
import { useSessionStore } from '@/stores/sessionStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useSessionRefresh } from '@/hooks/useSessionRefresh';

interface User {
  username: string;
  email?: string;
  account_type?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  setUser: (user: User | null) => void;
  logout: () => void;
  checkAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Session refresh: keep tokens alive while user is active
  const sessionTimeoutRaw = useUserStore(state => state.userSettings).sessionTimeout as number;
  const sessionTimeout = sessionTimeoutRaw === 0 ? 0 : (sessionTimeoutRaw || 30);
  useSessionRefresh({
    refreshInterval: 5 * 60 * 1000, // Check every 5 minutes
    inactivityThreshold: sessionTimeout === 0 ? 0 : sessionTimeout * 60 * 1000, // 0 = never timeout
    enabled: !!user && !loading,
  });

  // Helper function to clear user-specific localStorage
  const clearUserSpecificLocalStorage = (): void => {
    console.log('[Auth] Clearing user-specific data...');

    // Define all keys to clear
    const keysToClear = [
      'session_context',
      'active_drafts',
      'patents',
      'new_resource_ids',
      'new_resource_names',
      'new_pros_docs',
      'new_pros_doc_names',
      'folder_name',
      'open_folder_id',
      'session_applications',
      'open_draft_id',
      'history',
      'session_name',
      'session-storage',      // Chat messages and session data (Zustand sessionStore)
      'user-storage',         // User-specific storage data (Zustand userStore)
    ];

    // Clear all localStorage keys
    keysToClear.forEach(key => {
      localStorage.removeItem(key);
      console.log(`[Auth] Removed ${key}`);
    });

    // Reset Zustand stores to initial state
    // These will write empty/initial state to their persist keys
    useSessionStore.getState().resetSession();
    useNotificationStore.getState().clearAll();
    useUserStore.getState().clearUser();

    console.log('[Auth] Cleared user-specific localStorage and reset stores');
  };

  const checkAuth = async () => {
    console.log('Checking auth state...');
    try {
      // Get stored username before calling checkLogin
      const storedUsername = localStorage.getItem('username');

      // Call backend to validate and refresh token
      const response = await authApi.checkLogin();

      if (response && response.username) {
        const currentUsername = response.username;

        // DETECT USER SWITCH
        if (storedUsername && storedUsername !== currentUsername) {
          console.log(`[Auth] User switch detected: ${storedUsername} → ${currentUsername}`);

          // 1. Save previous user's state to backend
          await saveUserBrowserState(storedUsername);

          // 2. Clear user-specific localStorage (except auth tokens)
          clearUserSpecificLocalStorage();

          // 3. Load new user's state from backend
          await loadUserBrowserState(currentUsername);

          // 4. Reload page to reinitialize all components with new user's data
          console.log('[Auth] Reloading page to apply new user state...');
          window.location.reload();
          return; // Exit early since page is reloading
        }

        // Fetch account info to get account_type
        let accountType = response.account_type;
        let email = response.email;

        try {
          const accountInfo = await userApi.fetchAccountInfo();
          if (accountInfo && !accountInfo.error) {
            accountType = accountInfo.account_type || accountType;
            email = accountInfo.email || email;
          }
        } catch (error) {
          console.error('[AuthContext] Failed to fetch account info:', error);
        }

        console.log('[AuthContext] final account_type:', accountType);

        // Save account_type to localStorage for fallback
        if (accountType) {
          localStorage.setItem('account_type', accountType);
        }
        if (email) {
          localStorage.setItem('email', email);
        }

        setUser({
          username: response.username,
          email: email || localStorage.getItem('email') || undefined,
          account_type: accountType || localStorage.getItem('account_type') || undefined
        });
        console.log('User authenticated:', response.username);

        // Load user settings from backend after successful authentication
        useUserStore.getState().loadUserSettingsFromBackend();
      } else {
        // No valid session
        setUser(null);
        console.log('User not authenticated');
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const logout = async () => {
    console.log('Logging out...');

    // Store tokens before clearing (needed for save operation)
    const username = localStorage.getItem('username');
    const idToken = localStorage.getItem('id_token');

    // IMMEDIATELY clear auth tokens to stop all network calls
    console.log('[Auth] Clearing auth tokens immediately to stop network calls...');
    localStorage.removeItem('id_token');
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('account_type');

    // Save current state using stored token (happens in background, non-blocking)
    if (username && idToken) {
      saveUserBrowserState(username, idToken).catch(err => {
        console.error('[Auth] Failed to save browser state on logout:', err);
      });
    }

    // Clear user-specific localStorage and reset stores
    clearUserSpecificLocalStorage();
    // Account info is cached for a short window to stop six components each fetching
    // it on mount; dropping it here means the next sign-in cannot read the last one's.
    invalidateAccountInfo();

    setUser(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    setUser,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
